import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from './BrandSymbol';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import LoginModal from '../LoginModal';

interface HamburgerMenuProps {
  className?: string;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const { logoutSession } = useSessionPersistence();
  const navigate = useNavigate();

  // Standard menu items that appear on all logged-in pages
  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
      ),
      route: '/frame-140'
    },
    {
      id: 'plan',
      label: 'Plan',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      ),
      route: '/frame-196'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      ),
      route: '/settings'
    },
    {
      id: 'support',
      label: 'Support',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C17.759 8.071 18 9.009 18 10zm-9.525 4.994C7.592 14.76 6.55 14.448 5.595 13.888l1.525-1.524a4.002 4.002 0 002.183-.078l1.562 1.562zM8.5 12.5a1.5 1.5 0 113-3 1.5 1.5 0 01-3 3z" clipRule="evenodd" />
        </svg>
      ),
      route: '/frame-199'
    }
  ];

  const authMenuItem = {
    id: 'logout',
    label: 'Logout',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h6a1 1 0 110 2H5v10h5a1 1 0 110 2H4a1 1 0 01-1-1V4zm11.293 1.293a1 1 0 011.414 1.414L14.414 9H19a1 1 0 110 2h-4.586l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3z" clipRule="evenodd" />
      </svg>
    ),
    action: async () => {
      try {
        console.log('[HamburgerMenu] Initiating secure logout...');
        
        // 1. Clear authentication data
        localStorage.removeItem('account-token');
        localStorage.removeItem('account-refresh-token');
        
        // 2. Clear auth-related session data
        logoutSession();
        
        // 3. Close menu
        setIsOpen(false);
        
        // 4. Navigate to login page
        navigate('/login');
        
      } catch (error) {
        console.error('[HamburgerMenu] Logout error:', error);
        localStorage.removeItem('account-token');
        logoutSession();
        setIsOpen(false);
        navigate('/login');
      }
    }
  };

  // Always show all menu items and logout
  const fullMenuItems = menuItems;
  const showLogoutSection = true;

  const handleMenuItemClick = (route: string) => {
    navigate(route);
    setIsOpen(false);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hamburger Button */}
      <button
        onClick={toggleMenu}
        className="w-6 h-6 flex flex-col justify-center items-center space-y-1 hover:opacity-75 transition-opacity"
        aria-label="Toggle menu"
      >
        <div className={`w-5 h-0.5 bg-stone-500 transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
        <div className={`w-5 h-0.5 bg-stone-500 transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`} />
        <div className={`w-5 h-0.5 bg-stone-500 transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
      </button>

      {/* Menu Overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
            <div className="p-6">
              {/* Header with Logo */}
              <div className="flex items-center justify-between mb-8">
                <BrandSymbol size="2xl" variant="dark" showText />
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-6 h-6 text-stone-500 hover:text-stone-700 transition-colors"
                  aria-label="Close menu"
                >
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {/* Menu Items */}
              <nav className="space-y-2">
                {fullMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => item.route ? handleMenuItemClick(item.route) : item.action?.()}
                    className="w-full flex items-center gap-4 px-4 py-3 text-left rounded-lg transition-all duration-200 hover:bg-cyan-50 hover:shadow-sm group"
                  >
                    <div className="text-green-600 group-hover:text-cyan-700 transition-colors">
                      {item.icon}
                    </div>
                    <span className="text-stone-800 font-medium group-hover:text-cyan-700 transition-colors">
                      {item.label}
                    </span>
                  </button>
                ))}
              </nav>

              {/* Logout Section */}
              {showLogoutSection && (
                <div className="mt-8 pt-6 border-t border-stone-200">
                  <button
                    onClick={authMenuItem.action}
                    className="w-full flex items-center gap-4 px-4 py-3 text-left rounded-lg transition-all duration-200 hover:bg-cyan-50 hover:shadow-sm group"
                  >
                    <div className="text-green-600 group-hover:text-cyan-700 transition-colors">
                      {authMenuItem.icon}
                    </div>
                    <span className="text-stone-800 font-medium group-hover:text-cyan-700 transition-colors">
                      {authMenuItem.label}
                    </span>
                  </button>
                </div>
              )}

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-stone-200">
                <p className="text-xs text-stone-500 text-center">
                  End of an Era Estate Planning
                </p>
              </div>

              <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HamburgerMenu; 